import React, { Fragment, useState, useEffect } from 'react';
import TableBuilder, { ApiRequest, ButtonBuilder, ModalButtonBuilder, RegisteredInputsBuilder, FormOptionsBuilder, ToastBuilder } from '../../../../../../../GlobalComponents/Builders';
import { Row, Col, CardHeader, CardBody, Card, Form } from 'reactstrap';
import { H4 } from '../../../../../../../AbstractElements';
import { useForm } from 'react-hook-form';
import { buildOptionsFromArray, buildSelectOptionsFromArray } from '../../../../../../../GlobalComponents/Helpers';

const CATEGORY_OPTIONS = ['pending', 'processing', 'resolved', 'error', 'cancelled'];

const CATEGORY_COLORS = {
    pending: '#6c757d',
    processing: '#0d6efd',
    resolved: '#198754',
    error: '#dc3545',
    cancelled: '#ffc107'
};

const DataMigrationSettings = () => {
    const [settingsData, setSettingsData] = useState({ states: [], actions: [], codes: [] });
    const [isProcessing, setIsProcessing] = useState(true);
    const { register, handleSubmit, setValue, control, reset, formState: { errors } } = useForm();

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = () => {
        ApiRequest('GET', '/dmt/settings', { setIsProcessing }).then(function (results) {
            if (results.data && results.data.length > 0) {
                const data = results.data[0];
                setSettingsData({
                    states: data.states || [],
                    actions: data.actions || [],
                    codes: data.codes || []
                });
            }
        });
    };

    const handleStateUpdate = (formData, stateData) => {
        const patchData = formData;
        ApiRequest('PATCH', '/dmt/states/{stateId}', { pathParams: { stateId: stateData.state_id }, body: patchData }).then(function (results) {
            loadSettings();
            ToastBuilder('success', 'State updated successfully');
        });
    };

    const handleStateCreate = (formData, type) => {
        const createData = { ...formData, type };
        ApiRequest('POST', '/dmt/states', { body: createData }).then(function (results) {
            loadSettings();
            reset();
            ToastBuilder('success', `${type === 'status' ? 'Status' : 'Action'} created successfully`);
        });
    };

    return (
        <Fragment>
            <Row>
                <Col lg={12}>
                    <Card>
                        <CardHeader className='d-flex justify-content-between align-items-center'>
                            <H4 attrH4={{ className: 'card-title mb-0' }}>Record Statuses</H4>
                            <ModalButtonBuilder
                                title='Add Status'
                                modalTitle='Add New Status'
                                className='btn btn-hover-primary'
                                formId='add-status-form'
                                formIsValid={!errors.name}
                                formErrors={errors}
                                onClick={() => reset()}
                                body={
                                    <Form id='add-status-form' onSubmit={handleSubmit((data) => handleStateCreate(data, 'status'))}>
                                        <RegisteredInputsBuilder
                                            control={control}
                                            registrator={register}
                                            registratorErrors={errors}
                                            inputs={[
                                                {
                                                    label: 'Name',
                                                    required: true,
                                                    name: 'name',
                                                    type: 'text',
                                                    size: 12
                                                },
                                                {
                                                    label: 'Description',
                                                    name: 'description',
                                                    type: 'text',
                                                    size: 12
                                                },
                                                {
                                                    label: 'Sort Order',
                                                    name: 'sort_order',
                                                    type: 'number',
                                                    size: 12
                                                },
                                                {
                                                    label: 'Category',
                                                    name: 'category',
                                                    type: 'reactselect',
                                                    size: 12,
                                                    options: buildSelectOptionsFromArray(CATEGORY_OPTIONS, { labelIsValue: true })
                                                }
                                            ]}
                                        />
                                    </Form>
                                }
                            />
                        </CardHeader>
                        <CardBody>
                            <TableBuilder
                                data={settingsData.states}
                                processing={isProcessing}
                                pagination={false}
                                sortField={5}
                                columns={[
                                    {
                                        name: 'ID',
                                        selector: (row) => row.state_id,
                                        sortable: true,
                                        maxWidth: '8%',
                                        center: true,
                                    },
                                    {
                                        name: 'Name',
                                        selector: (row) => row.name,
                                        maxWidth: '15%',
                                        sortable: true,
                                    },
                                    {
                                        name: 'Description',
                                        selector: (row) => row.description,
                                        sortable: true,
                                    },
                                    {
                                        name: 'Category',
                                        selector: (row) => row.category ? <span className='badge' style={{ backgroundColor: CATEGORY_COLORS[row.category] }}>{row.category}</span> : '-',
                                        sortable: true,
                                        maxWidth: '12%',
                                        center: true,
                                        sortFunction: (a, b) => (a.category || '').localeCompare(b.category || ''),
                                    },
                                    {
                                        name: 'Sort Order',
                                        selector: (row) => row.sort_order,
                                        sortable: true,
                                        maxWidth: '10%',
                                        center: true,
                                    },
                                    {
                                        name: 'Actions',
                                        selector: (row) => (
                                            <ModalButtonBuilder
                                                title='Edit'
                                                modalTitle={`Edit Status: ${row.name}`}
                                                className='btn btn-outline-secondary my-1'
                                                formId={`edit-status-${row.state_id}`}
                                                onClick={() => {
                                                    setValue('name', row.name);
                                                    setValue('description', row.description || '');
                                                    setValue('sort_order', row.sort_order);
                                                    setValue('category', row.category || 'pending');
                                                }}
                                                body={
                                                    <Form id={`edit-status-${row.state_id}`} onSubmit={handleSubmit((data) => handleStateUpdate(data, row))}>
                                                        <RegisteredInputsBuilder
                                                            control={control}
                                                            registrator={register}
                                                            registratorErrors={errors}
                                                            inputs={[
                                                                {
                                                                    label: 'Name',
                                                                    required: true,
                                                                    name: 'name',
                                                                    type: 'text',
                                                                    size: 12
                                                                },
                                                                {
                                                                    label: 'Description',
                                                                    name: 'description',
                                                                    type: 'text',
                                                                    size: 12
                                                                },
                                                                {
                                                                    label: 'Sort Order',
                                                                    name: 'sort_order',
                                                                    type: 'number',
                                                                    size: 12
                                                                },
                                                                {
                                                                    label: 'Category',
                                                                    name: 'category',
                                                                    type: 'reactselect',
                                                                    size: 12,
                                                                    options: buildSelectOptionsFromArray(CATEGORY_OPTIONS, { labelIsValue: true })
                                                                }
                                                            ]}
                                                        />
                                                    </Form>
                                                }
                                            />
                                        ),
                                        center: true,
                                        maxWidth: '15%'
                                    },
                                ]}
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col lg={12}>
                    <Card>
                        <CardHeader className='d-flex justify-content-between align-items-center'>
                            <H4 attrH4={{ className: 'card-title mb-0' }}>Record Actions</H4>
                            <ModalButtonBuilder
                                title='Add Action'
                                modalTitle='Add New Action'
                                className='btn btn-hover-primary'
                                formId='add-action-form'
                                formIsValid={!errors.name}
                                formErrors={errors}
                                onClick={() => reset()}
                                body={
                                    <Form id='add-action-form' onSubmit={handleSubmit((data) => handleStateCreate(data, 'action'))}>
                                        <RegisteredInputsBuilder
                                            registrator={register}
                                            registratorErrors={errors}
                                            inputs={[
                                                {
                                                    label: 'Name',
                                                    required: true,
                                                    name: 'name',
                                                    type: 'text',
                                                    size: 12
                                                },
                                                {
                                                    label: 'Description',
                                                    name: 'description',
                                                    type: 'text',
                                                    size: 12
                                                },
                                                {
                                                    label: 'Sort Order',
                                                    name: 'sort_order',
                                                    type: 'number',
                                                    size: 12
                                                }
                                            ]}
                                        />
                                    </Form>
                                }
                            />
                        </CardHeader>
                        <CardBody>
                            <TableBuilder
                                data={settingsData.actions}
                                processing={isProcessing}
                                pagination={false}
                                sortField={4}
                                columns={[
                                    {
                                        name: 'ID',
                                        selector: (row) => row.state_id,
                                        sortable: true,
                                        maxWidth: '8%',
                                        center: true,
                                    },
                                    {
                                        name: 'Name',
                                        selector: (row) => row.name,
                                        maxWidth: '15%',
                                        sortable: true,
                                    },
                                    {
                                        name: 'Description',
                                        selector: (row) => row.description,
                                        sortable: true,
                                    },
                                    {
                                        name: 'Sort Order',
                                        selector: (row) => row.sort_order,
                                        sortable: true,
                                        maxWidth: '10%',
                                        center: true,
                                    },
                                    {
                                        name: 'Actions',
                                        selector: (row) => (
                                            <ModalButtonBuilder
                                                title='Edit'
                                                modalTitle={`Edit Action: ${row.name}`}
                                                className='btn btn-outline-secondary my-1'
                                                formId={`edit-action-${row.state_id}`}
                                                onClick={() => {
                                                    setValue('name', row.name);
                                                    setValue('description', row.description || '');
                                                    setValue('sort_order', row.sort_order);
                                                }}
                                                body={
                                                    <Form id={`edit-action-${row.state_id}`} onSubmit={handleSubmit((data) => handleStateUpdate(data, row))}>
                                                        <RegisteredInputsBuilder
                                                            registrator={register}
                                                            registratorErrors={errors}
                                                            inputs={[
                                                                {
                                                                    label: 'Name',
                                                                    required: true,
                                                                    name: 'name',
                                                                    type: 'text',
                                                                    size: 12
                                                                },
                                                                {
                                                                    label: 'Description',
                                                                    name: 'description',
                                                                    type: 'text',
                                                                    size: 12
                                                                },
                                                                {
                                                                    label: 'Sort Order',
                                                                    name: 'sort_order',
                                                                    type: 'number',
                                                                    size: 12
                                                                }
                                                            ]}
                                                        />
                                                    </Form>
                                                }
                                            />
                                        ),
                                        center: true,
                                        maxWidth: '15%'
                                    },
                                ]}
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col lg={12}>
                    <Card>
                        <CardHeader>
                            <H4 attrH4={{ className: 'card-title mb-0' }}>Mapping Codes</H4>
                        </CardHeader>
                        <CardBody>
                            <TableBuilder
                                data={settingsData.codes}
                                processing={isProcessing}
                                pagination={false}
                                columns={[
                                    {
                                        name: 'ID',
                                        selector: (row) => row.code_id,
                                        sortable: true,
                                        maxWidth: '8%',
                                        center: true
                                    },
                                    {
                                        name: 'Code',
                                        selector: (row) => row.code,
                                        sortable: true
                                    },
                                    {
                                        name: 'Color',
                                        selector: (row) => <span className={`badge`} style={{ backgroundColor: row.color }}>{row.color}</span>,
                                        sortable: true
                                    },
                                    {
                                        name: 'Help Text',
                                        selector: (row) => row.helptext,
                                        sortable: true
                                    },
                                ]}
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Fragment>
    );
};

export default DataMigrationSettings;